import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { Root, Thumb } from '../primitives/slider-2d-primitive/slider-2d-primitive.js';
import { useAllStrings } from '../settings/settings.strings.js';
import { hsvToCss, hslToCss } from '../utils/color.js';
import { getVarName } from '../utils/css.js';
import { useCanvasRenderer } from './color-area.hooks.js';
import { renderColorAreaHSB, renderColorAreaHSL } from './color-area.render.js';
import { fill } from '../styles/thumb.css.js';
import { canvas, root, variant, thumb, thumbShadow, thumbBg } from './color-area.css.js';

const fillVar = getVarName(fill);
/**
 * A 2D color area for picking saturation and brightness at a given hue.
 * Renders a canvas-based HSV gradient background with a draggable thumb.
 *
 * ```
 *  white ┌────────┐ saturated color
 *        │        │
 *   gray │   ◉    │ dark color
 *        │        │
 *  black └────────┘ black
 * ```
 */
const HSB = forwardRef(({ hue, saturation, brightness, onChange: onChangeIn, colorSpace = 'srgb', variant: variant$1 = 'inset', size = 'fill', 'aria-valuetext': ariaValuetext, ...props }, ref) => {
    const canvasRef = useRef(null);
    const strings = useAllStrings();
    const sat = Math.round(saturation * 100);
    const brt = Math.round(brightness * 100);
    const combinedValuetext = useMemo(() => strings.colorAreaHSB({ saturation: sat, brightness: brt }), [strings, sat, brt]);
    const satValuetext = useMemo(() => strings.saturationValue({ value: sat }), [strings, sat]);
    const brtValuetext = useMemo(() => strings.brightnessValue({ value: brt }), [strings, brt]);
    // Invert Y: brightness 1 is at the top (y=0), brightness 0 is at the bottom (y=1).
    const value = useMemo(() => ({ x: saturation, y: 1 - brightness }), [saturation, brightness]);
    const onChange = useCallback((newValue, other) => {
        onChangeIn({ saturation: newValue.x, brightness: 1 - newValue.y }, other);
    }, [onChangeIn]);
    useCanvasRenderer(canvasRef, hue, colorSpace, renderColorAreaHSB);
    return (jsxs(Root, { "data-fpl-component": "", ...props, ref: ref, "aria-valuetext": ariaValuetext ?? combinedValuetext, ariaValuetextX: ariaValuetext ?? satValuetext, ariaValuetextY: ariaValuetext ?? brtValuetext, className: clsx(root, variant[variant$1]), style: size === 'fill' ? { width: '100%' } : { width: size, height: size }, value: value, precision: "infinite", onChange: onChange, children: [jsx("canvas", { ref: canvasRef, className: canvas, "aria-hidden": "true" }), jsx(ColorAreaThumb, { fill: hsvToCss(hue, saturation, brightness) })] }));
});
HSB.displayName = 'ColorArea.HSB';
/**
 * A 2D color area for picking saturation and lightness at a given hue.
 * Renders a canvas-based HSL gradient background with a draggable thumb.
 *
 * ```
 *  white ┌────────┐ white
 *        │        │
 *   gray │   ◉    │ saturated color
 *        │        │
 *  black └────────┘ black
 * ```
 */
const HSL = forwardRef(({ hue, saturation, lightness, onChange: onChangeIn, colorSpace = 'srgb', variant: variant$1 = 'inset', size = 'fill', 'aria-valuetext': ariaValuetext, ...props }, ref) => {
    const canvasRef = useRef(null);
    const strings = useAllStrings();
    const sat = Math.round(saturation * 100);
    const ltn = Math.round(lightness * 100);
    const combinedValuetext = useMemo(() => strings.colorAreaHSL({ saturation: sat, lightness: ltn }), [strings, sat, ltn]);
    const satValuetext = useMemo(() => strings.saturationValue({ value: sat }), [strings, sat]);
    const ltnValuetext = useMemo(() => strings.lightnessValue({ value: ltn }), [strings, ltn]);
    // Invert Y: lightness 1 is at the top (y=0), lightness 0 is at the bottom (y=1).
    const value = useMemo(() => ({ x: saturation, y: 1 - lightness }), [saturation, lightness]);
    const onChange = useCallback((newValue, other) => {
        onChangeIn({ saturation: newValue.x, lightness: 1 - newValue.y }, other);
    }, [onChangeIn]);
    useCanvasRenderer(canvasRef, hue, colorSpace, renderColorAreaHSL);
    return (jsxs(Root, { "data-fpl-component": "", ...props, ref: ref, "aria-valuetext": ariaValuetext ?? combinedValuetext, ariaValuetextX: ariaValuetext ?? satValuetext, ariaValuetextY: ariaValuetext ?? ltnValuetext, className: clsx(root, variant[variant$1]), style: size === 'fill' ? { width: '100%' } : { width: size, height: size }, value: value, precision: "infinite", onChange: onChange, children: [jsx("canvas", { ref: canvasRef, className: canvas, "aria-hidden": "true" }), jsx(ColorAreaThumb, { fill: hslToCss(hue, saturation, lightness) })] }));
});
HSL.displayName = 'ColorArea.HSL';
/** The draggable knob with its drop shadow, filled with the currently-picked `fill` color. */
// eslint-disable-next-line react/display-name -- private helper, not rendered under the ColorArea namespace
function ColorAreaThumb({ fill }) {
    return (jsxs(Thumb, { "data-fpl-component": "", className: thumb, children: [jsx("div", { className: thumbShadow }), jsx("div", { className: thumbBg, style: { [fillVar]: fill } })] }));
}

export { HSB, HSL };
//# sourceMappingURL=color-area.js.map
